import { Checkbox, DatePicker, Input, InputNumber, Radio, Select, Spin } from 'antd';
import RangeSliderField from './RangeSliderField.jsx';
import DateRangePresetField from './DateRangePresetField.jsx';
import KeywordsField from './KeywordsField.jsx';
import { hasKeywordValue, isPrimaryKeywordField } from './keywordCriteria';
import { getLocationMasterOptions } from '../../services/adminApi';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

// Every renderer is called with the same props, so a new input type never has to
// re-implement option fetching, disabled handling or Form wiring:
//   { field, value, onChange, options, optionsLoading, disabled, placeholder }
//
// Registry entry flags:
//   booleanEligible — admins may mark fields of this type "Boolean capable"
//   needsOptions    — SearchFieldControl resolves field.dataSource before rendering
//   multiValue      — the control emits an array, mapped onto several API params
//   ownsLabel       — (field) => bool; the renderer draws its own heading and
//                     SearchBuilderV1 omits the Form.Item label
//   hasValue        — (value, ctx) => bool; needed by any type whose value is a
//                     composite object, which is always truthy and would
//                     otherwise keep the Search button permanently enabled
//   fetchOptions    — ({ field, search, moduleName }) => Promise<option[]>; a type
//                     whose options come from a source of its own rather than the
//                     generic dataSource endpoint. SearchFieldControl calls this
//                     instead of getDropdownValues, so renderers still never touch
//                     the network
//   remoteSearch    — the list is narrowed server-side, so the renderer is handed an
//                     `onSearch` prop and must turn antd's local filtering off
//                     (filterOption={false})
export const FIELD_TYPE_REGISTRY = {};

export function registerFieldType(type, definition) {
  if (!type || typeof definition?.render !== 'function') {
    throw new Error('registerFieldType(type, { render }) requires a type name and a render function');
  }
  FIELD_TYPE_REGISTRY[type] = {
    booleanEligible: false,
    needsOptions: false,
    multiValue: false,
    ...definition,
  };
  return FIELD_TYPE_REGISTRY[type];
}

export function getFieldType(type) {
  return FIELD_TYPE_REGISTRY[type] ?? FIELD_TYPE_REGISTRY.text;
}

export function isBooleanEligible(type) {
  return Boolean(getFieldType(type).booleanEligible);
}

// A type may draw its own heading instead of relying on the Form.Item label,
// and may decide for itself whether it holds a value. Both default to the old
// behaviour, so every existing type is unaffected.
export function ownsLabelFor(field) {
  const owns = getFieldType(field?.type ?? 'text').ownsLabel;
  return typeof owns === 'function' ? Boolean(owns(field)) : Boolean(owns);
}

export function fieldHasValue(field, value, context) {
  const predicate = getFieldType(field?.type ?? 'text').hasValue;
  if (typeof predicate !== 'function') return undefined;
  return Boolean(predicate(value, context));
}

export function getBooleanEligibleTypes() {
  return Object.entries(FIELD_TYPE_REGISTRY)
    .filter(([, definition]) => definition.booleanEligible)
    .map(([type]) => type);
}

function enterText(field) {
  return `Enter ${String(field.label ?? field.field ?? '').toLowerCase()}`;
}

// Select multiplicity is config-driven and resolved one way for every type that
// renders a Select — the search-side counterpart of AddFormV1's selectMode.
function selectModeFor(field) {
  return (field.multiSelect || field.mode === 'multiple') ? 'multiple' : undefined;
}

function selectText(field) {
  return `Select ${String(field.label ?? field.field ?? '').toLowerCase()}`;
}

registerFieldType('text', {
  booleanEligible: false,
  render: ({ field, value, onChange, disabled, placeholder }) => (
    <Input
      value={value ?? ''}
      disabled={disabled}
      placeholder={placeholder ?? enterText(field)}
      allowClear
      onChange={(event) => onChange(event.target.value)}
    />
  ),
});

registerFieldType('email', {
  booleanEligible: false,
  render: ({ field, value, onChange, disabled, placeholder }) => (
    <Input
      type="email"
      value={value ?? ''}
      disabled={disabled}
      placeholder={placeholder ?? enterText(field)}
      allowClear
      onChange={(event) => onChange(event.target.value)}
    />
  ),
});

registerFieldType('textarea', {
  booleanEligible: false,
  render: ({ field, value, onChange, disabled, placeholder }) => (
    <TextArea
      rows={field.rows ?? 3}
      value={value ?? ''}
      disabled={disabled}
      placeholder={placeholder ?? enterText(field)}
      onChange={(event) => onChange(event.target.value)}
    />
  ),
});

registerFieldType('number', {
  render: ({ field, value, onChange, disabled, placeholder }) => (
    <InputNumber
      style={{ width: '100%' }}
      value={value ?? null}
      disabled={disabled}
      min={field.min}
      max={field.max}
      placeholder={placeholder ?? enterText(field)}
      onChange={onChange}
    />
  ),
});

registerFieldType('select', {
  booleanEligible: false,
  needsOptions: true,
  render: ({ field, value, onChange, options, optionsLoading, disabled, placeholder }) => (
    <Select
      mode={selectModeFor(field)}
      value={value ?? undefined}
      disabled={disabled}
      options={options}
      loading={optionsLoading}
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder={placeholder ?? selectText(field)}
      notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
      onChange={onChange}
    />
  ),
});

registerFieldType('multiselect', {
  booleanEligible: false,
  needsOptions: true,
  multiValue: true,
  render: ({ field, value, onChange, options, optionsLoading, disabled, placeholder }) => (
    <Select
      mode="multiple"
      value={value ?? undefined}
      disabled={disabled}
      options={options}
      loading={optionsLoading}
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder={placeholder ?? selectText(field)}
      notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
      onChange={onChange}
    />
  ),
});

// type:"location" means the SAME thing in a search config as it does in an add or
// edit one: "City, State, Country" straight from the locationMasters collection,
// searched server-side as the user types, with nothing else to configure. It used
// to declare dataSource "locationMaster", but the generic /filter-dropdown-values
// endpoint only understands "master" — the request fell through to the module's own
// stored values (or to a lookup configured on the field) and the dropdown never
// listed a real location. getLocationMasterOptions is the same
// /admin/location-dropdown-values source AddFormV1's location branch uses.
registerFieldType('location', {
  needsOptions: true,
  remoteSearch: true,
  fetchOptions: ({ field, search }) => getLocationMasterOptions(search, field.limit ?? 50),
  render: ({ field, value, onChange, onSearch, options, optionsLoading, disabled, placeholder }) => (
    <Select
      value={value ?? undefined}
      disabled={disabled}
      // Single or multi is admin config — the same `multiSelect` flag the add form
      // reads. An array value already OR-joins in buildBooleanRequest.
      mode={selectModeFor(field)}
      options={options}
      loading={optionsLoading}
      allowClear
      showSearch
      // The location master is world-wide and paged, so antd must not filter the
      // page it was handed — the server already did the narrowing.
      filterOption={false}
      placeholder={placeholder ?? selectText(field)}
      notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
      onSearch={onSearch}
      onChange={onChange}
    />
  ),
});

// Free-text multi-entry: type a value, press Enter, it becomes a chip. No
// longer boolean eligible — only the common Keywords field can become an
// expression box (see the `keywords` type below). A tagify field an admin
// wants mandatory stars and exclusions on is switched to `keywords`.
registerFieldType('tagify', {
  booleanEligible: false,
  multiValue: true,
  render: ({ field, value, onChange, disabled, placeholder }) => (
    <Select
      mode="tags"
      value={value ?? undefined}
      disabled={disabled}
      open={false}
      suffixIcon={null}
      allowClear
      tokenSeparators={[',']}
      placeholder={placeholder ?? enterText(field)}
      onChange={onChange}
    />
  ),
});

// A "locked" option (admin-configured, off by default — see FormGroupsSection
// Options/Values editor) is pre-selected and cannot be deselected, e.g.
// "Internal" always included in Search Preference. Radio only allows one
// selection at all, so a locked radio option fixes the whole group to it
// (disabled) rather than mixing "one fixed, others free" into a
// mutually-exclusive control where that has no real meaning.
registerFieldType('radio', {
  needsOptions: true,
  render: ({ value, onChange, options, disabled }) => {
    const locked = options.find((option) => option.locked);
    return (
      <Radio.Group
        className="sbv1-option-row"
        value={locked ? locked.value : value}
        disabled={disabled || Boolean(locked)}
        options={options}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  },
});

// An "All" checkbox is automatic, not admin-authored: any checkbox field
// with more than one option gets one, unless the field explicitly opts out
// (field.hideSelectAll — FormGroupsSection's one field-level switch). It is
// never part of `options` and never enters the submitted value — its
// checked state is computed fresh every render from whether every real
// option is currently selected, so it can never visibly disagree with
// reality the way a stored trigger flag could. `locked` (per-option,
// admin-configured, unrelated to this) still composes for free, since the
// derived state reads from the already-locked-merged value.
registerFieldType('checkbox', {
  needsOptions: true,
  multiValue: true,
  render: ({ field, value, onChange, options, disabled }) => {
    if (options.length === 0) {
      return (
        <Checkbox
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        >
          {field.checkboxLabel ?? field.label}
        </Checkbox>
      );
    }

    const lockedValues = options.filter((option) => option.locked).map((option) => option.value);
    const allValues = options.map((option) => option.value);
    const current = Array.isArray(value) ? value : [];

    // Every read and write goes through here, so the stored value is always in
    // the admin's configured option order. Downstream this value IS the
    // source-tab order (SearchBuilderV1 -> getSearchSources -> buildSourceTabs),
    // and the merges below concatenate lockedValues onto whatever came in — so a
    // locked value merged into a stored or seeded value could otherwise land out
    // of order and reorder the tab strip. antd already hands the group's value
    // back in options order, so this is a guarantee at the boundary rather than
    // a fix for a live bug; the tab reordering itself was buildSourceTabs.
    const orderByOptions = (values) => {
      const wanted = new Set(values);
      return allValues.filter((option) => wanted.has(option));
    };

    // Seeds locked values in even before any Form initialValue has resolved,
    // and guards against them ever being missing from what's displayed.
    const effectiveValue = orderByOptions([...current, ...lockedValues]);
    const groupOptions = options.map((option) => (
      option.locked ? { ...option, disabled: true } : option
    ));

    const handleGroupChange = (next) => {
      onChange(orderByOptions([...next, ...lockedValues]));
    };

    const showSelectAll = field.hideSelectAll !== true && options.length > 1;
    const allChecked = showSelectAll && allValues.every((v) => effectiveValue.includes(v));
    const handleSelectAllChange = (event) => {
      onChange(event.target.checked ? orderByOptions(allValues) : orderByOptions(lockedValues));
    };

    // One wrapping row — "All" is the first item of it, styled exactly like an
    // option (sbv1-option-row, in SearchBuilderV1.css). It used to be a vertical
    // Space, which cost the form a whole extra row and made "All" read as a
    // heading over the options.
    return (
      <div className="sbv1-option-row sbv1-checkbox-with-select-all">
        {showSelectAll && (
          <Checkbox checked={allChecked} disabled={disabled} onChange={handleSelectAllChange}>
            All
          </Checkbox>
        )}
        <Checkbox.Group
          value={effectiveValue}
          disabled={disabled}
          options={groupOptions}
          onChange={handleGroupChange}
        />
      </div>
    );
  },
});

registerFieldType('date', {
  render: ({ value, onChange, disabled, placeholder }) => (
    <DatePicker
      style={{ width: '100%' }}
      value={value ?? null}
      disabled={disabled}
      placeholder={placeholder ?? 'Select date'}
      onChange={onChange}
    />
  ),
});

registerFieldType('daterange', {
  multiValue: true,
  render: ({ value, onChange, disabled }) => (
    <RangePicker
      style={{ width: '100%' }}
      value={value ?? null}
      disabled={disabled}
      onChange={onChange}
    />
  ),
});

// The experience "dragger" — one config field, two values.
registerFieldType('range', {
  multiValue: true,
  render: (props) => <RangeSliderField {...props} />,
});

// Preset dropdown (Today/Yesterday/.../Custom Range) that reveals a
// RangePicker for Custom Range. Not booleanEligible — the boolean-search API
// has no date parameter at all (Be_Auth_DevOps/models/boolean_search.go), so
// this field can never contribute to a boolean request in any mapping.
registerFieldType('daterangePreset', {
  multiValue: true,
  render: (props) => <DateRangePresetField {...props} />,
});

// The starred-chip control — the only boolean-eligible type. It backs the
// common Keywords box AND any other field an admin turns chips/stars on for
// (Skills today), which is why the mandatory switch, the group it feeds and
// the paths it targets are all per-field config rather than baked in here.
// Its value is a composite object holding both the chips and the expression,
// hence hasValue.
registerFieldType('keywords', {
  booleanEligible: true,
  multiValue: true,
  // Only the primary field draws its own heading (label + Boolean toggle);
  // a secondary one like Skills keeps the normal Form.Item label.
  ownsLabel: (field) => isPrimaryKeywordField(field),
  hasValue: hasKeywordValue,
  render: (props) => <KeywordsField {...props} />,
});
